import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/vault", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/vault";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-ink flex flex-col lg:grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-14 bg-ink-deep border-r border-gold/15 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.08)_0%,transparent_60%)]" />
        <Link to="/" data-testid="login-back-home" className="relative font-mono text-[11px] tracking-[0.2em] uppercase text-gold/70 hover:text-gold flex items-center gap-2 transition-colors">
          <ArrowLeft size={14} /> Return to Public Portal
        </Link>
        <div className="relative">
          <img src={LOGO_URL} alt="7 Generations Indigenous Trust" className="w-full max-w-sm object-contain" />
        </div>
        <p className="relative font-mono text-[10px] tracking-[0.3em] uppercase text-gold/50">
          Preservation • Stewardship • Prosperity
        </p>
      </div>

      {/* Right auth panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="w-full max-w-md"
        >
          <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.28em] uppercase text-gold">
            <Lock size={14} /> Restricted Access
          </div>
          <h1 className="font-serif font-light text-5xl tracking-tight mt-6">The Vault</h1>
          <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-5">
            Enter the Modern Freedmen Bureau — your secure constituent dashboard for blockchain-backed
            ancestry records, digital economic citizenship credentials, and mutual-aid networks.
          </p>

          <button
            onClick={handleLogin}
            data-testid="google-login-btn"
            className="w-full mt-10 flex items-center justify-center gap-3 px-8 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-gold-text transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#000" d="M24 9.5c3.9 0 6.6 1.7 8.1 3.1l5.9-5.7C34.6 3.5 29.8 1.5 24 1.5 14.6 1.5 6.5 6.9 2.6 14.7l6.9 5.4C11.3 14 17.1 9.5 24 9.5z"/><path fill="#000" d="M46.1 24.5c0-1.6-.1-2.8-.4-4H24v7.6h12.7c-.3 2-1.6 5-4.7 7l7.2 5.6c4.3-4 6.9-9.9 6.9-16.2z"/><path fill="#000" d="M9.5 28.1c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-6.9-5.4C1 16.6 0 20.2 0 23.5s1 6.9 2.6 10l6.9-5.4z"/><path fill="#000" d="M24 47.5c5.8 0 10.7-1.9 14.2-5.2l-7.2-5.6c-1.9 1.3-4.5 2.3-7 2.3-6.9 0-12.7-4.5-14.5-10.6l-6.9 5.4C6.5 41.1 14.6 47.5 24 47.5z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-start gap-3 mt-8 text-[#7a766f]">
            <ShieldCheck size={16} className="text-gold/60 mt-0.5 shrink-0" />
            <p className="font-sans text-xs leading-relaxed">
              Sessions are encrypted and privately held. Access is limited to verified members of the Trust.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
